import { Container, Graphics, Texture, TilingSprite } from "pixi.js";
import type {
  CityMapData,
  MapRect,
  PixiMapSource,
  PlotData,
  RoadRect,
} from "../api/types";
import { IsoProjection } from "../core/projection";
import { createCityGroundPattern } from "./groundPattern";
import { drawShorelines } from "./shorelineRenderer";
import { createWaterTexture, mergeWaterCells } from "./waterTerrain";

export const CITY_GROUND_EXTENT_MULTIPLIER = 6;
export const CITY_EDITOR_DEVELOPMENT_EXTENT = 182;

export function cityGroundExtent(extent: number): number {
  return Math.max(12, extent) * CITY_GROUND_EXTENT_MULTIPLIER;
}

function color(value: string | undefined, fallback: number): number {
  if (value && /^#[0-9a-f]{6}$/i.test(value)) {
    return Number.parseInt(value.slice(1), 16);
  }
  if (value && /^#[0-9a-f]{3}$/i.test(value)) {
    const [r, g, b] = value.slice(1).split("");
    return Number.parseInt(`${r}${r}${g}${g}${b}${b}`, 16);
  }
  return fallback;
}

function contains(rect: MapRect, x: number, y: number): boolean {
  return x >= rect.x &&
    x <= rect.x + rect.width &&
    y >= rect.y &&
    y <= rect.y + rect.height;
}

export class CityMap extends Container {
  private readonly materialTextures = new Map<string, Texture>();
  private waterTexture: Texture | null = null;
  private waterHighlightTexture: Texture | null = null;
  private waterFlow: [TilingSprite, TilingSprite] | null = null;

  updateWater(seconds: number): void {
    if (!this.waterFlow || this.data.water?.animate === false) return;
    // Two independently drifting ribbons create actual surface motion, not
    // the previous brightness-only pulsing. Both are clipped to the river.
    const [near, far] = this.waterFlow;
    near.tilePosition.set(seconds * 12 + 2 * Math.sin(seconds * 0.8), seconds * 4.8);
    far.tilePosition.set(-seconds * 6.2, seconds * 7.6 + 2 * Math.cos(seconds * 0.65));
    near.alpha = 0.78 + 0.09 * Math.sin(seconds * 0.7);
    far.alpha = 0.46 + 0.08 * Math.cos(seconds * 0.9);
  }

  private materialFill(graphics: Graphics, base: number, alpha: number) {
    // Render the entire ground with one repeating isometric texture. Keep
    // parks, paths, asphalt and all individual assets on their own materials.
    if (graphics.label === "city-ground") {
      const key = "full-city-isometric-ground";
      let texture = this.materialTextures.get(key);
      if (!texture) {
        texture = Texture.from(createCityGroundPattern());
        texture.source.style.addressMode = "repeat";
        this.materialTextures.set(key, texture);
      }
      return { color: 0xffffff, alpha, texture, textureSpace: "global" as const };
    }

    const grass = graphics.label === "city-surfaces" &&
      base === color(this.data.palette.park ?? this.data.palette.grass, 0x4b754c);
    const asphalt = graphics.label === "city-roads";
    if (!grass && !asphalt) return { color: base, alpha };
    const key = `${grass ? "grass" : "asphalt"}-${base}`;
    let texture = this.materialTextures.get(key);
    if (!texture) {
      // Small, seamless material tiles are generated once, not during animation.
      const size = grass ? 256 : 128;
      const canvas = document.createElement("canvas"); canvas.width = canvas.height = size;
      const context = canvas.getContext("2d");
      if (!context) return { color: base, alpha };
      const pixels = context.createImageData(size, size);
      const channels = [(base >> 16) & 255, (base >> 8) & 255, base & 255];
      let seed = 77191;
      for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        const noise = seed / 4294967296 - .5;
        const u = x / size * Math.PI * 2, v = y / size * Math.PI * 2;
        const patch = (Math.sin(u + .7 * Math.sin(v)) + Math.cos(v * 2 + Math.sin(u)) + .5 * Math.sin(u * 3 - v * 2)) / 2.5;
        const shade = grass ? patch * 14 + noise * 18 : patch * 3 + noise * 14;
        const dry = grass ? Math.max(0, patch) * .16 : 0;
        const offset = (y * size + x) * 4;
        for (let c = 0; c < 3; c++) {
          const soil = [155, 137, 91][c];
          pixels.data[offset + c] = Math.max(0, Math.min(255, channels[c] * (1 - dry) + soil * dry + shade));
        }
        pixels.data[offset + 3] = 255;
      }
      context.putImageData(pixels, 0, 0);
      texture = Texture.from(canvas);
      texture.source.style.addressMode = "repeat";
      this.materialTextures.set(key, texture);
    }
    return { color: 0xffffff, alpha, texture, textureSpace: "global" as const };
  }

  override destroy(options?: Parameters<Container["destroy"]>[0]): void {
    super.destroy(options);
    for (const texture of this.materialTextures.values()) texture.destroy(true);
    this.materialTextures.clear();
    this.waterTexture?.destroy(true);
    this.waterTexture = null;
    this.waterHighlightTexture?.destroy(true);
    this.waterHighlightTexture = null;
  }

  constructor(
    private data: CityMapData,
    _plots: PlotData[],
    private readonly projection: IsoProjection,
    private readonly editorMode = false,
  ) {
    super();
    this.label = "map";
    this.draw();
  }

  updateSource(source: PixiMapSource): void {
    if ((source.water?.color ?? "#137c96") !== (this.data.water?.color ?? "#137c96")) {
      this.waterTexture?.destroy(true);
      this.waterTexture = null;
    }
    const roads = source.segments.map((road) => ({ ...road }));
    const sidewalks = roads
      .filter(
        (road) =>
          road.sidewalk !== false && (road.shape ?? "straight") === "straight",
      )
      .map((road) => ({
        id: `sidewalk-${road.id}`,
        x: road.x - source.sidewalkWidth,
        y: road.y - source.sidewalkWidth,
        width: road.width + source.sidewalkWidth * 2,
        height: road.height + source.sidewalkWidth * 2,
      }));

    const intersections: MapRect[] = [];
    const straightRoads = roads.filter(
      (road) => (road.shape ?? "straight") === "straight",
    );
    const horizontals = straightRoads.filter((road) => road.axis === "h");
    const verticals = straightRoads.filter((road) => road.axis === "v");
    for (const horizontal of horizontals) {
      const hx0 = horizontal.x;
      const hx1 = horizontal.x + horizontal.width;
      const hy0 = horizontal.y;
      const hy1 = horizontal.y + horizontal.height;
      for (const vertical of verticals) {
        const vx0 = vertical.x;
        const vx1 = vertical.x + vertical.width;
        const vy0 = vertical.y;
        const vy1 = vertical.y + vertical.height;
        const x0 = Math.max(hx0, vx0);
        const x1 = Math.min(hx1, vx1);
        const y0 = Math.max(hy0, vy0);
        const y1 = Math.min(hy1, vy1);
        if (x0 >= x1 || y0 >= y1) continue;
        intersections.push({
          id: `junction-${horizontal.id}-${vertical.id}`,
          x: x0,
          y: y0,
          width: x1 - x0,
          height: y1 - y0,
        });
      }
    }

    this.data = {
      version: source.version,
      extent: source.extent,
      tileWidth: source.tileWidth,
      tileHeight: source.tileHeight,
      background: source.background,
      palette: source.palette,
      sidewalkWidth: source.sidewalkWidth,
      roads,
      sidewalks,
      intersections,
      plazas: source.plazas.map((item) => ({ ...item })),
      parks: source.parks.map((item) => ({ ...item })),
      water: source.water ? { ...source.water, cells: source.water.cells.map(cell => [...cell] as [number, number]) } : undefined,
    };

    for (const child of this.removeChildren()) child.destroy();
    this.draw();
  }

  private rectPoints(rect: MapRect): number[] {
    const a = this.projection.worldToScreen(rect.x, rect.y);
    const b = this.projection.worldToScreen(rect.x + rect.width, rect.y);
    const c = this.projection.worldToScreen(
      rect.x + rect.width,
      rect.y + rect.height,
    );
    const d = this.projection.worldToScreen(rect.x, rect.y + rect.height);
    return [a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y];
  }

  private drawRect(
    graphics: Graphics,
    rect: MapRect,
    fill: number,
    alpha = 1,
    border?: { color: number; width: number; alpha?: number },
  ): void {
    graphics.poly(this.rectPoints(rect)).fill(this.materialFill(graphics, fill, alpha));
    if (border) {
      graphics.stroke({
        color: border.color,
        width: border.width,
        alpha: border.alpha ?? 1,
      });
    }
  }

  private isIntersection(x: number, y: number): boolean {
    return this.data.intersections.some((item) => contains(item, x, y));
  }

  private projectPolygon(points: Array<{ x: number; y: number }>): number[] {
    return points.flatMap((point) => {
      const projected = this.projection.worldToScreen(point.x, point.y);
      return [projected.x, projected.y];
    });
  }

  private drawWorldPolygon(
    graphics: Graphics,
    points: Array<{ x: number; y: number }>,
    fill: number,
    alpha = 1,
  ): void {
    if (points.length < 3) return;
    graphics.poly(this.projectPolygon(points)).fill(this.materialFill(graphics, fill, alpha));
  }

  private diskPoints(
    cx: number,
    cy: number,
    radius: number,
    steps = 48,
  ): Array<{ x: number; y: number }> {
    return Array.from({ length: steps }, (_, index) => {
      const angle = (Math.PI * 2 * index) / steps;
      return {
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
      };
    });
  }

  private ringSectorPoints(
    cx: number,
    cy: number,
    innerRadius: number,
    outerRadius: number,
    startAngle: number,
    endAngle: number,
    steps = 24,
  ): Array<{ x: number; y: number }> {
    const outer = Array.from({ length: steps + 1 }, (_, index) => {
      const angle = startAngle + ((endAngle - startAngle) * index) / steps;
      return {
        x: cx + Math.cos(angle) * outerRadius,
        y: cy + Math.sin(angle) * outerRadius,
      };
    });
    const inner = Array.from({ length: steps + 1 }, (_, index) => {
      const angle = endAngle - ((endAngle - startAngle) * index) / steps;
      return {
        x: cx + Math.cos(angle) * innerRadius,
        y: cy + Math.sin(angle) * innerRadius,
      };
    });
    return [...outer, ...inner];
  }

  private curveGeometry(road: RoadRect): {
    cx: number;
    cy: number;
    start: number;
    end: number;
    radius: number;
  } | null {
    const shape = road.shape ?? "straight";
    const radius = Math.max(1, Math.min(road.width, road.height));
    if (shape === "curve-ne") {
      return { cx: road.x, cy: road.y + road.height, start: -Math.PI / 2, end: 0, radius };
    }
    if (shape === "curve-nw") {
      return { cx: road.x + road.width, cy: road.y + road.height, start: Math.PI, end: Math.PI * 1.5, radius };
    }
    if (shape === "curve-se") {
      return { cx: road.x, cy: road.y, start: 0, end: Math.PI / 2, radius };
    }
    if (shape === "curve-sw") {
      return { cx: road.x + road.width, cy: road.y, start: Math.PI / 2, end: Math.PI, radius };
    }
    return null;
  }

  private specialRoadThickness(road: RoadRect): number {
    const size = Math.min(road.width, road.height);
    const limit = (road.shape ?? "straight") === "roundabout"
      ? size * 0.24
      : size * 0.48;
    return Math.max(
      0.5,
      Math.min(
        road.thickness ?? Math.min(road.width, road.height, 1.25),
        limit,
      ),
    );
  }

  private drawSpecialRoadBase(
    graphics: Graphics,
    road: RoadRect,
    fill: number,
    sidewalk = false,
  ): void {
    const shape = road.shape ?? "straight";
    if (shape === "straight") return;
    const sidewalkWidth = sidewalk ? (this.data.sidewalkWidth ?? 0.78) : 0;
    const thickness = this.specialRoadThickness(road);

    const curve = this.curveGeometry(road);
    if (curve) {
      const outer = curve.radius + sidewalkWidth;
      const inner = Math.max(
        0.15,
        curve.radius - thickness - sidewalkWidth,
      );
      this.drawWorldPolygon(
        graphics,
        this.ringSectorPoints(
          curve.cx,
          curve.cy,
          inner,
          outer,
          curve.start,
          curve.end,
        ),
        fill,
      );
      return;
    }

    if (shape !== "roundabout") return;
    const cx = road.x + road.width / 2;
    const cy = road.y + road.height / 2;
    const size = Math.min(road.width, road.height);
    const outerRadius = Math.min(
      size * 0.43,
      Math.max(thickness * 1.35, size * 0.33),
    );
    const innerRadius = Math.max(0.35, outerRadius - thickness);
    const outer = outerRadius + sidewalkWidth;
    const inner = Math.max(0.18, innerRadius - sidewalkWidth);
    const connectorWidth = thickness + sidewalkWidth * 2;
    const halfConnector = connectorWidth / 2;

    // Four road mouths connect the ring to normal straight roads.
    this.drawRect(
      graphics,
      {
        id: `${road.id}-north-mouth`,
        x: cx - halfConnector,
        y: road.y - sidewalkWidth,
        width: connectorWidth,
        height: Math.max(0.1, cy - outerRadius - road.y + thickness * 0.65 + sidewalkWidth),
      },
      fill,
    );
    this.drawRect(
      graphics,
      {
        id: `${road.id}-south-mouth`,
        x: cx - halfConnector,
        y: cy + outerRadius - thickness * 0.65,
        width: connectorWidth,
        height: Math.max(
          0.1,
          road.y + road.height - cy - outerRadius + thickness * 0.65 + sidewalkWidth,
        ),
      },
      fill,
    );
    this.drawRect(
      graphics,
      {
        id: `${road.id}-west-mouth`,
        x: road.x - sidewalkWidth,
        y: cy - halfConnector,
        width: Math.max(0.1, cx - outerRadius - road.x + thickness * 0.65 + sidewalkWidth),
        height: connectorWidth,
      },
      fill,
    );
    this.drawRect(
      graphics,
      {
        id: `${road.id}-east-mouth`,
        x: cx + outerRadius - thickness * 0.65,
        y: cy - halfConnector,
        width: Math.max(
          0.1,
          road.x + road.width - cx - outerRadius + thickness * 0.65 + sidewalkWidth,
        ),
        height: connectorWidth,
      },
      fill,
    );
    this.drawWorldPolygon(
      graphics,
      this.ringSectorPoints(cx, cy, inner, outer, 0, Math.PI * 2, 56),
      fill,
    );

    if (sidewalk) {
      // Plain paved central island; intentionally no flowers or vegetation.
      const islandRadius = Math.max(0.12, innerRadius - sidewalkWidth);
      this.drawWorldPolygon(
        graphics,
        this.diskPoints(cx, cy, islandRadius),
        fill,
      );
    }
  }

  private drawRoundaboutIsland(
    graphics: Graphics,
    road: RoadRect,
    fill: number,
  ): void {
    if ((road.shape ?? "straight") !== "roundabout") return;
    const cx = road.x + road.width / 2;
    const cy = road.y + road.height / 2;
    const thickness = this.specialRoadThickness(road);
    const size = Math.min(road.width, road.height);
    const outerRadius = Math.min(
      size * 0.43,
      Math.max(thickness * 1.35, size * 0.33),
    );
    const innerRadius = Math.max(0.35, outerRadius - thickness);
    const sidewalkWidth = road.sidewalk === false
      ? 0
      : (this.data.sidewalkWidth ?? 0.78);
    const islandRadius = Math.max(0.12, innerRadius - sidewalkWidth);
    this.drawWorldPolygon(
      graphics,
      this.diskPoints(cx, cy, islandRadius),
      fill,
    );
  }

  private drawSpecialRoadMarkings(graphics: Graphics, road: RoadRect): void {
    const shape = road.shape ?? "straight";
    if (shape === "straight") return;
    const marking = color(this.data.palette.roadMarking, 0xeadca8);
    const thickness = this.specialRoadThickness(road);

    const curve = this.curveGeometry(road);
    if (curve) {
      const radius = Math.max(0.2, curve.radius - thickness / 2);
      const dashes = 5;
      for (let index = 0; index < dashes; index += 1) {
        const center = curve.start + ((curve.end - curve.start) * (index + 0.5)) / dashes;
        const half = (curve.end - curve.start) / (dashes * 5);
        const a = this.projection.worldToScreen(
          curve.cx + Math.cos(center - half) * radius,
          curve.cy + Math.sin(center - half) * radius,
        );
        const b = this.projection.worldToScreen(
          curve.cx + Math.cos(center + half) * radius,
          curve.cy + Math.sin(center + half) * radius,
        );
        graphics
          .moveTo(a.x, a.y)
          .lineTo(b.x, b.y)
          .stroke({ color: marking, width: 1.35, alpha: 0.9 });
      }
      return;
    }

    if (shape !== "roundabout") return;
    const cx = road.x + road.width / 2;
    const cy = road.y + road.height / 2;
    const size = Math.min(road.width, road.height);
    const outerRadius = Math.min(
      size * 0.43,
      Math.max(thickness * 1.35, size * 0.33),
    );
    const centerRadius = Math.max(0.3, outerRadius - thickness / 2);
    const dashes = 12;
    for (let index = 0; index < dashes; index += 1) {
      const center = (Math.PI * 2 * (index + 0.5)) / dashes;
      const half = Math.PI / (dashes * 3.4);
      const a = this.projection.worldToScreen(
        cx + Math.cos(center - half) * centerRadius,
        cy + Math.sin(center - half) * centerRadius,
      );
      const b = this.projection.worldToScreen(
        cx + Math.cos(center + half) * centerRadius,
        cy + Math.sin(center + half) * centerRadius,
      );
      graphics
        .moveTo(a.x, a.y)
        .lineTo(b.x, b.y)
        .stroke({ color: marking, width: 1.35, alpha: 0.9 });
    }

    const mouthInset = Math.max(0.35, thickness * 0.35);
    const mouthInner = Math.max(0.1, outerRadius - thickness * 0.35);
    const mouthSegments = [
      [
        { x: cx, y: road.y + mouthInset },
        { x: cx, y: cy - mouthInner },
      ],
      [
        { x: cx, y: cy + mouthInner },
        { x: cx, y: road.y + road.height - mouthInset },
      ],
      [
        { x: road.x + mouthInset, y: cy },
        { x: cx - mouthInner, y: cy },
      ],
      [
        { x: cx + mouthInner, y: cy },
        { x: road.x + road.width - mouthInset, y: cy },
      ],
    ];
    mouthSegments.forEach(([from, to]) => {
      const a = this.projection.worldToScreen(from.x, from.y);
      const b = this.projection.worldToScreen(to.x, to.y);
      graphics
        .moveTo(a.x, a.y)
        .lineTo(b.x, b.y)
        .stroke({ color: marking, width: 1.2, alpha: 0.82 });
    });
  }

  private drawRoadMarkings(graphics: Graphics, road: RoadRect): void {
    const marking = color(this.data.palette.roadMarking, 0xeadca8);
    const spacing = road.kind === "avenue" ? 2.5 : 3.1;
    const length = road.axis === "h" ? road.width : road.height;
    const start = road.axis === "h" ? road.x : road.y;
    const cross = road.axis === "h"
      ? road.y + road.height / 2
      : road.x + road.width / 2;

    for (let offset = 1.1; offset < length - 0.75; offset += spacing) {
      const worldCenter = start + offset;
      const cx = road.axis === "h" ? worldCenter : cross;
      const cy = road.axis === "h" ? cross : worldCenter;
      if (this.isIntersection(cx, cy)) continue;

      const half = road.kind === "avenue" ? 0.55 : 0.40;
      const a = road.axis === "h"
        ? this.projection.worldToScreen(worldCenter - half, cross)
        : this.projection.worldToScreen(cross, worldCenter - half);
      const b = road.axis === "h"
        ? this.projection.worldToScreen(worldCenter + half, cross)
        : this.projection.worldToScreen(cross, worldCenter + half);

      graphics
        .moveTo(a.x, a.y)
        .lineTo(b.x, b.y)
        .stroke({ color: marking, width: 1.35, alpha: 0.9 });
    }
  }

  private draw(): void {
    const ground = new Graphics();
    const surfaces = new Graphics();
    const roads = new Graphics();
    const markings = new Graphics();
    const editBoundary = new Graphics();
    ground.label = "city-ground";
    surfaces.label = "city-surfaces";
    roads.label = "city-roads";

    // Expand only the visual green ground. Roads, plots, parks, objects and
    // all authored world coordinates keep their current size and positions.
    const groundExtent = cityGroundExtent(this.data.extent);
    this.drawRect(
      ground,
      {
        id: "city-ground",
        x: -groundExtent,
        y: -groundExtent,
        width: groundExtent * 2,
        height: groundExtent * 2,
      },
      color(this.data.palette.ground, 0x557c50),
      1,
    );

    const cellSize = this.data.water?.cellSize === 0.25 ? 0.25 : 1;
    const waterRects = mergeWaterCells(this.data.water?.cells ?? [], cellSize);
    const water = new Graphics();
    const shores = new Graphics();
    const flowContainer = new Container();
    const flowMask = new Graphics();
    this.waterFlow = null;
    if (waterRects.length) {
      const waterColor = this.data.water?.color ?? "#137c96";
      if (!this.waterTexture) {
        this.waterTexture = Texture.from(createWaterTexture(waterColor));
        this.waterTexture.source.style.addressMode = "repeat";
      }
      const animate = this.data.water?.animate !== false;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      // One set of merged polygons for the base fill, ground cuts and flow mask.
      // Intact land receives no animated graphics or hidden water.
      for (const rect of waterRects) {
        const points = this.rectPoints(rect);
        water.poly(points).fill({
          texture: this.waterTexture,
          textureSpace: "global",
          color: 0xffffff,
          alpha: 0.96,
        });
        ground.poly(points).cut();
        if (animate) {
          flowMask.poly(points).fill({ color: 0xffffff });
          for (let i = 0; i < points.length; i += 2) {
            minX = Math.min(minX, points[i]);
            minY = Math.min(minY, points[i + 1]);
            maxX = Math.max(maxX, points[i]);
            maxY = Math.max(maxY, points[i + 1]);
          }
        }
      }
      if (animate) {
        if (!this.waterHighlightTexture) {
          this.waterHighlightTexture = Texture.from(createWaterTexture(waterColor, true));
          this.waterHighlightTexture.source.style.addressMode = "repeat";
        }
        const width = Math.max(1, maxX - minX), height = Math.max(1, maxY - minY);
        const near = new TilingSprite({ texture: this.waterHighlightTexture, width, height });
        const far = new TilingSprite({ texture: this.waterHighlightTexture, width, height });
        near.position.set(minX, minY);
        far.position.set(minX, minY);
        near.alpha = 0.78;
        far.alpha = 0.54;
        far.tilePosition.set(97, 71);
        flowContainer.addChild(far, near);
        flowContainer.mask = flowMask;
        this.waterFlow = [near, far];
      }
      if (this.data.water?.shore !== false) {
        drawShorelines(shores, this.data.water?.cells ?? [], cellSize, this.projection,
          this.materialFill(ground, color(this.data.palette.ground, 0x557c50), 1));
      }
    }

    if (this.editorMode) {
      const editableExtent = CITY_EDITOR_DEVELOPMENT_EXTENT;
      const editableRect: MapRect = {
        id: "city-edit-boundary",
        x: -editableExtent,
        y: -editableExtent,
        width: editableExtent * 2,
        height: editableExtent * 2,
      };
      const points = this.rectPoints(editableRect);

      // High-contrast double stroke around the real admin development area.
      // Asset/object coordinates and admin-created plots are limited to this
      // same development extent by both the editor and backend.
      // It exists only inside the editor and is never rendered for visitors.
      editBoundary
        .poly(points)
        .stroke({ color: 0x172033, width: 8, alpha: 0.92 })
        .poly(points)
        .stroke({
          color: 0xffb020,
          width: 2,
          alpha: 1,
          pixelLine: true,
        } as any);
      editBoundary.label = "city-edit-boundary";
    }

    for (const park of this.data.parks ?? []) {
      this.drawRect(
        surfaces,
        park,
        color(this.data.palette.park ?? this.data.palette.grass, 0x4b754c),
      );
    }

    for (const plaza of this.data.plazas) {
      this.drawRect(
        surfaces,
        plaza,
        color(this.data.palette.plaza, 0xd4c394),
        1,
        {
          color: color(this.data.palette.groundBorder, 0x3f6543),
          width: 0.8,
          alpha: 0.55,
        },
      );
    }

    for (const sidewalk of this.data.sidewalks) {
      this.drawRect(
        surfaces,
        sidewalk,
        color(this.data.palette.sidewalk, 0xaeb6b1),
      );
    }

    for (const road of this.data.roads) {
      this.drawRoundaboutIsland(
        surfaces,
        road,
        color(this.data.palette.sidewalk, 0xaeb6b1),
      );
    }

    for (const road of this.data.roads) {
      if ((road.shape ?? "straight") !== "straight" && road.sidewalk !== false) {
        this.drawSpecialRoadBase(
          surfaces,
          road,
          color(this.data.palette.sidewalk, 0xaeb6b1),
          true,
        );
      }
    }

    for (const road of this.data.roads) {
      const roadColor = color(
        road.kind === "avenue"
          ? this.data.palette.road
          : this.data.palette.roadAlt,
        0x41494b,
      );
      if ((road.shape ?? "straight") === "straight") {
        this.drawRect(roads, road, roadColor);
        this.drawRoadMarkings(markings, road);
      } else {
        this.drawSpecialRoadBase(roads, road, roadColor, false);
        this.drawSpecialRoadMarkings(markings, road);
      }
    }

    // Repaint junctions after every road so intersections always read as one
    // continuous asphalt surface and never expose sidewalk wedges.
    for (const intersection of this.data.intersections) {
      this.drawRect(
        roads,
        intersection,
        color(this.data.palette.road, 0x41494b),
      );
    }

    ground.label = "city-ground";
    surfaces.label = "city-surfaces";
    roads.label = "city-roads";
    markings.label = "road-markings";
    this.addChild(water, flowContainer, flowMask, ground, shores, surfaces, roads, markings);
    if (this.editorMode) this.addChild(editBoundary);
  }
}
